<div [class.container-fluid]="!embedded" [class.p-3]="!embedded" [class.pv-page]="!embedded">
  <!-- Toolbar nur im Standalone-Modus. Im Editor-Embed haengt die Preview
       in einer eigenen Spalte direkt im Editor-Layout. -->
  @if (!embedded) {
    <div class="bp-toolbar d-flex align-items-center gap-2 mb-3">
      <button type="button" class="btn btn-outline-secondary btn-sm" (click)="back()">
        <i class="bi bi-arrow-left"></i> Zurück
      </button>
      @if (entry) {
        <span class="badge bg-light text-dark border ms-2">
          {{ entry.hauptbodenform.abkuerzung || '–' }}
        </span>
        <strong class="ms-1">{{ entry.hauptbodenform.name || '(ohne Name)' }}</strong>
      }
      <span class="ms-auto d-flex align-items-center gap-2">
        <button type="button" class="btn btn-outline-secondary btn-sm"
                (click)="toggleOriginal()"
                [class.active]="showOriginal"
                title="Original-PDF-Seite zum Vergleich dazublenden (wird vom Server lazy geladen)">
          <i class="bi bi-file-image"></i>
          {{ showOriginal ? 'Original aus' : 'Original' }}
        </button>
        <span class="badge bg-secondary">Vorschau (read-only)</span>
      </span>
    </div>
  }

  <div class="pv-content">
  @if (loading) {
    <div class="text-muted">Lade…</div>
  } @else if (notFound) {
    <div class="alert alert-warning">
      Steckbrief nicht gefunden. Eventuell wurde er gelöscht oder die Liste ist
      noch nicht importiert/geladen.
    </div>
  } @else if (entry) {

    <div class="pv-layout" [class.pv-with-original]="showOriginal && !embedded">
      @if (showOriginal && !embedded) {
        <div class="pv-original-pane">
          <app-sea-original [pdf]="entry.pdf" [page]="entry.page"></app-sea-original>
        </div>
      }
    <div class="pv-frame">
      <!-- Header: HBF-Name | Abkuerzung | Substratgruppe -->
      <div class="pv-row pv-header">
        <div class="pv-cell pv-title-cell">
          <div class="pv-title">
            @for (n of splitSlashed(entry.hauptbodenform.name); track $index; let last = $last) {
              {{ n }}@if (!last) {<br>}
            }
            @if (!entry.hauptbodenform.name) { &ndash; }
          </div>
        </div>
        <div class="pv-cell pv-abk-cell">
          <div class="pv-abk">
            @for (a of splitSlashed(entry.hauptbodenform.abkuerzung); track $index; let last = $last) {
              {{ a }}@if (!last) {<br>}
            }
            @if (!entry.hauptbodenform.abkuerzung) { &ndash; }
          </div>
        </div>
        <div class="pv-cell pv-suffix-corner" title="Substratgruppe der Hauptbodenform">
          {{ entry.hauptbodenform.substratgruppe || '–' }}
        </div>
      </div>

      <!-- Mittelblock: links AUSSCHLIESSLICH Substrat, rechts Horizont-
           Diagramm(e). Alle weiteren Hinweise (Humus, Eisen, Bänder, ...,
           Hydromorphie zuletzt) sitzen in einer eigenen Zeile darunter. -->
      <div class="pv-row pv-middle">
        <div class="pv-cell pv-left-stack">
          <div class="pv-block pv-substrat-block">
            @for (layer of substratLayers; track $index) {
              <div class="pv-substrat-symbol">{{ layer.kuerzel || '–' }}</div>
              @for (para of substratParagraphs(layer.beschreibung); track $index) {
                <div class="pv-substrat-desc">{{ para }}</div>
              }
            }
          </div>
        </div>

        <div class="pv-cell pv-diagram-cell">
          <div class="pv-diagram-row">
            @for (diag of diagramme; track $index) {
              @let nRows = (diag.horizonte.length > diag.zonen.length ? diag.horizonte.length : diag.zonen.length);
              <div class="pv-horizont">
                @for (i of range(nRows || 3); track $index) {
                  @let hi = diag.horizonte[$index];
                  @let zi = diag.zonen[$index];
                  <span class="pv-h-sym">
                    @if (symOf(hi)) {
                      {{ symOf(hi) }}
                      @if (noteOf(hi)) { <span class="pv-h-note">{{ noteOf(hi) }}</span> }
                    } @else if (showArrow(diag, $index, 'horizonte')) {
                      <span class="pv-arrow" aria-hidden="true">↓</span>
                    }
                  </span>
                  <span class="pv-z-sym">
                    @if (symOf(zi)) {
                      {{ symOf(zi) }}
                      @if (noteOf(zi)) { <span class="pv-z-note">{{ noteOf(zi) }}</span> }
                    } @else if (showArrow(diag, $index, 'zonen')) {
                      <span class="pv-arrow" aria-hidden="true">↓</span>
                    }
                  </span>
                }
              </div>
            }
            @if (diagramme.length === 0) {
              <div class="text-muted small fst-italic">kein Horizont-Diagramm</div>
            }
          </div>
        </div>
      </div>

      <!-- Hinweis-Blöcke unter Substrat/Diagramm-Zeile. Reihenfolge:
           Humus, dann alle übrigen Sektionen, dann Hydromorphie als letztes
           direkt oberhalb der Schraffur-Tabelle. -->
      @if (humusItems.length || humusLines.length || extraLeftSections.length || extraDataBlocks.length || hydromorphieLines.length) {
        <div class="pv-hints">
          @if (humusItems.length || humusLines.length) {
            <div class="pv-block pv-humus-block">
              <div class="pv-block-title">Humus</div>
              @for (it of humusItems; track $index) {
                <div class="pv-block-line">{{ it.key }}: {{ it.value }}</div>
              }
              @for (ln of humusLines; track $index) {
                <div class="pv-block-line">{{ ln }}</div>
              }
            </div>
          }

          @for (sec of extraLeftSections; track $index) {
            <div class="pv-block pv-extra-block">
              @if (sec.title) { <div class="pv-block-title">{{ sec.title }}</div> }
              @for (it of sec.items; track $index) {
                <div class="pv-block-line">{{ it.key }}: {{ it.value }}</div>
              }
              @for (ln of sec.lines; track $index) {
                <div class="pv-block-line">{{ ln }}</div>
              }
            </div>
          }

          <!-- Auffangnetz: alle sonst nicht dargestellten Felder (Regel: ALLE
               Daten der HBF anzeigen). -->
          @for (blk of extraDataBlocks; track $index) {
            <div class="pv-block pv-extra-block">
              @if (blk.title) { <div class="pv-block-title">{{ blk.title }}</div> }
              @for (it of blk.items; track $index) {
                <div class="pv-block-line">{{ it.key }}: {{ it.value }}</div>
              }
              @for (ln of blk.lines; track $index) {
                <div class="pv-block-line">{{ ln }}</div>
              }
            </div>
          }

          @if (hydromorphieLines.length) {
            <div class="pv-block pv-hydromorphie-block">
              <div class="pv-block-title">Hydromorphiehinweise</div>
              @for (ln of hydromorphieLines; track $index) {
                <div class="pv-block-line">{{ ln }}</div>
              }
            </div>
          }
        </div>
      }

      <!-- Schraffur-Tabelle: in scrollbaren Wrapper, sonst laeuft sie bei
           vielen Schraffur-Spalten ueber den 760px-Frame rechts raus. -->
      @if (schrafCols.length) {
        @if (hasActiveSort) {
          <div class="pv-sort-reset">
            <button type="button" class="btn btn-outline-secondary btn-sm" (click)="resetSort()"
                    title="Sortierung aufheben und zur Original-Reihenfolge des Steckbriefs zurück">
              <i class="bi bi-arrow-counterclockwise"></i> Sortierung zurücksetzen
            </button>
          </div>
        }
        <div class="pv-table-wrap">
        <table class="pv-table">
          <colgroup>
            <col class="col-fbf">
            <col class="col-abk">
            @for (c of schrafCols; track c) { <col class="col-schraf"> }
            @if (hasFootnoteCol) { <col class="col-fn-ref"> }
            <col class="col-nks">
            <col class="col-5tel">
            <col class="col-fb">
          </colgroup>
          <thead>
            <!-- Zeile 0: Fixspalten links + Top-Level-Gruppen + Fixspalten rechts -->
            <tr>
              <th [attr.rowspan]="headerMaxLevel" class="pv-fixcol pv-lokal-th pv-sortable"
                  (click)="setSort('lokalname')"
                  title="Sortieren nach Lokalname (Klick zum Toggle/Reset)">
                Lokalname <span class="pv-sort-ind">{{ sortIcon('lokalname') }}</span>
              </th>
              <th [attr.rowspan]="headerMaxLevel" class="pv-fixcol pv-abk-th pv-sortable"
                  (click)="setSort('abkuerzung')"
                  title="Sortieren nach Abkuerzung">
                Abk. <span class="pv-sort-ind">{{ sortIcon('abkuerzung') }}</span>
              </th>
              @for (grp of headerRows[0]; track $index) {
                @let isLeaf = (headerMaxLevel === 1);
                @let h = grp.orientation !== 'v';
                @if (isLeaf) {
                  <th [attr.colspan]="grp.cols.length"
                      [class]="h ? 'h-cell' : (grp.label.length <= 2 ? 'vh-short' : 'vh')"
                      [class.pv-sortable]="grp.sortable"
                      [class.pv-sort-active]="isSchrafSorted(grp.sortCols)"
                      [class.pv-sort-desc]="isSchrafSorted(grp.sortCols) && sortDir === 'desc'"
                      (click)="grp.sortable && setSortSchraf(grp.sortCols)"
                      [title]="grp.sortable ? 'Sortieren: markierte Zeilen zuerst (Klick: auf / ab / zurück)' : null">
                    <span [style.font-size.px]="grp.fontPx">{{ displayLabel(grp.label) }}</span>
                  </th>
                } @else {
                  <th [attr.colspan]="grp.cols.length"
                      [attr.rowspan]="grp.rowspan && grp.rowspan > 1 ? grp.rowspan : null"
                      [class]="h ? 'h-group' : 'vh-group'"
                      [class.pv-sortable]="grp.sortable"
                      [class.pv-sort-active]="isSchrafSorted(grp.sortCols)"
                      [class.pv-sort-desc]="isSchrafSorted(grp.sortCols) && sortDir === 'desc'"
                      (click)="grp.sortable && setSortSchraf(grp.sortCols)"
                      [title]="grp.sortable ? 'Sortieren: markierte Zeilen zuerst (Klick: auf / ab / zurück)' : null">
                    <span [style.font-size.px]="grp.fontPx">{{ displayLabel(grp.label) }}</span>
                  </th>
                }
              }
              @if (hasFootnoteCol) {
                <th [attr.rowspan]="headerMaxLevel" class="col-center-cell pv-fixcol" title="Fußnoten-Referenz">Fn.</th>
              }
              <th [attr.rowspan]="headerMaxLevel" class="col-center-cell pv-fixcol pv-fix-vmiddle pv-sortable"
                  (click)="setSort('nahrkraftstufe')" title="Sortieren nach NKS">
                NKS <span class="pv-sort-ind">{{ sortIcon('nahrkraftstufe') }}</span>
              </th>
              <th [attr.rowspan]="headerMaxLevel" class="col-center-cell pv-fixcol pv-fix-vmiddle pv-sortable"
                  (click)="setSort('funftelstufe')" title="Sortieren nach Fuenftelstufe">
                5tel <span class="pv-sort-ind">{{ sortIcon('funftelstufe') }}</span>
              </th>
              <th [attr.rowspan]="headerMaxLevel" class="pv-fixcol pv-fix-vmiddle pv-sortable"
                  (click)="setSort('forstbetrieb')" title="Sortieren nach Forstbetrieb">
                Forstbetrieb des Taufprofils <span class="pv-sort-ind">{{ sortIcon('forstbetrieb') }}</span>
              </th>
            </tr>
            <!-- weitere Header-Zeilen (Sub-Gruppen / Leaf) -->
            @for (rowIdx of range(headerMaxLevel - 1); track $index) {
              @let lvl = $index + 1;
              @let isLeaf = (lvl === headerMaxLevel - 1);
              <tr>
                @for (grp of headerRows[lvl]; track $index) {
                  @let h = grp.orientation !== 'v';
                  @if (isLeaf) {
                    <th [attr.colspan]="grp.cols.length"
                        [attr.rowspan]="grp.rowspan && grp.rowspan > 1 ? grp.rowspan : null"
                        [class]="h ? 'h-cell' : (grp.label.length <= 2 ? 'vh-short' : 'vh')"
                        [class.pv-sortable]="grp.sortable"
                        [class.pv-sort-active]="isSchrafSorted(grp.sortCols)"
                        [class.pv-sort-desc]="isSchrafSorted(grp.sortCols) && sortDir === 'desc'"
                        (click)="grp.sortable && setSortSchraf(grp.sortCols)"
                        [title]="grp.sortable ? 'Sortieren: markierte Zeilen zuerst (Klick: auf / ab / zurück)' : null">
                      <span [style.font-size.px]="grp.fontPx">{{ displayLabel(grp.label) }}</span>
                    </th>
                  } @else {
                    <th [attr.colspan]="grp.cols.length"
                        [attr.rowspan]="grp.rowspan && grp.rowspan > 1 ? grp.rowspan : null"
                        [class]="h ? 'h-group' : 'vh-group'"
                        [class.pv-sortable]="grp.sortable"
                        [class.pv-sort-active]="isSchrafSorted(grp.sortCols)"
                        [class.pv-sort-desc]="isSchrafSorted(grp.sortCols) && sortDir === 'desc'"
                        (click)="grp.sortable && setSortSchraf(grp.sortCols)"
                        [title]="grp.sortable ? 'Sortieren: markierte Zeilen zuerst (Klick: auf / ab / zurück)' : null">
                      <span [style.font-size.px]="grp.fontPx">{{ displayLabel(grp.label) }}</span>
                    </th>
                  }
                }
              </tr>
            }
          </thead>
          <tbody>
            @for (f of sortedFbfs; track $index) {
              <tr [class.pv-struck]="f.gestrichen">
                <td>{{ f.lokalname }}</td>
                <td>{{ (f.abkuerzung || '') + (entry.fbf_klassensuffix || '') }}</td>
                @for (col of schrafCols; track col) {
                  @let st = cellState(f, col);
                  <td [class]="cellClass(st)">{{ cellText(st) }}</td>
                }
                @if (hasFootnoteCol) {
                  <td class="col-center-cell">{{ f.footnote_ref || '' }}</td>
                }
                <td class="col-center-cell">{{ f.nahrkraftstufe || '' }}</td>
                <td class="col-center-cell">{{ f.funftelstufe || '' }}</td>
                <td>{{ f.forstbetrieb || '' }}</td>
              </tr>
            }
          </tbody>
        </table>
        </div>
      } @else {
        <div class="text-muted small p-3 fst-italic">keine Schraffur-Spalten</div>
      }

      @if (fussnotenLines.length) {
        <div class="pv-footnotes">
          @for (ln of fussnotenLines; track $index) {
            <div class="pv-footnote-line">{{ ln }}</div>
          }
        </div>
      }

      <div class="pv-footer">
        <div>{{ klassifikation }}</div>
        <div class="text-end">{{ seitenzahl }}</div>
      </div>
    </div>
    </div>

  }
  </div>
</div>
